import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { useKeepMailOnDevice } from './keepOnDevice';
import './offline-notices.css';

/**
 * The two per-view surfaces offline reading needs.
 *
 * Neither of them is an error. A folder that was never opened while there was
 * a connection is not a failure — it is a thing that was never fetched, and
 * saying "couldn't load" about it teaches the user to distrust an app that is
 * working exactly as designed. Hence `role="status"`, not `role="alert"`, and
 * copy that says what is true.
 */

/** Ticks a component once a minute, so "4 minutes ago" does not lie for an hour. */
export function useMinuteTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

export function relativeTime(at: number, now: number): string {
  const seconds = Math.round((now - at) / 1000);
  if (seconds < 60) return 'just now';
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return format.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return format.format(-hours, 'hour');
  return format.format(-Math.round(hours / 24), 'day');
}

/** "Last synced 4 minutes ago" — the most reassuring line in an offline client. */
export function SyncedAtLine({ at }: { at: number | undefined }) {
  const now = useMinuteTick();
  if (at === undefined || !Number.isFinite(at) || at <= 0) return null;
  return (
    <span className="offline-synced" data-testid="last-synced">
      Last synced {relativeTime(at, now)}
    </span>
  );
}

/**
 * One word each, because this line sits beside the mail it describes and is
 * always followed by "Last synced …". The shell's banner is where the sentence
 * explaining offline belongs; repeating it here only crowds a narrow column.
 */
const STATUS_COPY: Record<ReturnType<typeof useConnectionStatus>, string> = {
  online: 'Online',
  offline: 'Offline',
  reconnecting: 'Reconnecting…',
};

/**
 * The three-state connection line, next to the mail it describes.
 *
 * The shell's own indicator lives in `components/OfflineIndicator.tsx` and is
 * another change's to move to three states; this reads the same source, so the
 * two cannot disagree once it does.
 */
export function ConnectionStatusLine({ syncedAt }: { syncedAt?: number }) {
  const status = useConnectionStatus();
  const synced = syncedAt !== undefined && Number.isFinite(syncedAt) && syncedAt > 0;
  return (
    <span className="offline-status" data-status={status} data-testid="connection-status">
      <span className="offline-status__mark" aria-hidden="true" />
      {/*
        Only the state is live (#205): "Offline" and "Reconnecting…" are news.
        The whole line used to be the live region, so "Last synced 3 minutes
        ago" was read out again every minute, and after every list refresh.
      */}
      <span className="offline-status__label" role="status">
        {STATUS_COPY[status]}
      </span>
      {synced ? (
        <SyncedAtLine at={syncedAt} />
      ) : (
        // Holds the line's shape until the first sync, so "Last synced …"
        // arriving does not re-wrap the filter row and shove the list down
        // (part of the phone's 0.25 layout shift on /mail).
        status === 'online' && <span className="offline-synced">Syncing…</span>
      )}
    </span>
  );
}

const WHAT_COPY = {
  message: {
    title: 'This message isn’t available offline',
    detail: 'It was never opened while there was a connection, so there is no copy on this device.',
  },
  folder: {
    title: 'This folder isn’t available offline',
    detail: 'It was never opened while there was a connection, so there is no copy on this device.',
  },
  list: {
    title: 'This view isn’t available offline',
    detail: 'It was never loaded while there was a connection, so there is no copy on this device.',
  },
} as const;

/**
 * The same three places, on a device that keeps no mail (ADR 0060 §4).
 *
 * "It was never opened while there was a connection" is not merely unhelpful
 * here, it is false: the message *was* opened, and nothing was kept because
 * the user asked for nothing to be kept. An app that recites the cache
 * explanation to someone who turned the cache off is pretending to be
 * offline-capable, which is the one thing this feature exists not to do. So
 * the notice names the setting, and where to change it.
 */
const NOT_KEPT_COPY = {
  title: 'Mail isn’t kept on this device',
  detail:
    'You turned off “Keep mail on this device for offline use”, so there is no copy here to read without a connection. Change it in Settings → Privacy.',
} as const;

export function NotAvailableOffline({
  what,
  onRetry,
  onBack,
}: {
  what: keyof typeof WHAT_COPY;
  onRetry?: () => void;
  onBack?: () => void;
}) {
  const status = useConnectionStatus();
  const [keep] = useKeepMailOnDevice();
  const copy = keep ? WHAT_COPY[what] : NOT_KEPT_COPY;
  return (
    <div
      className="empty-state"
      role="status"
      data-testid="not-available-offline"
      data-kept-on-device={keep ? 'true' : 'false'}
    >
      <Icon name="offline" size={32} />
      <p className="empty-state__title">{copy.title}</p>
      <p className="muted">{copy.detail}</p>
      <div className="empty-state__actions">
        {onBack && (
          <button type="button" className="btn" onClick={onBack}>
            Back to list
          </button>
        )}
        {onRetry && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={onRetry}
            // Nothing to retry against while the browser knows there is no
            // network: an enabled button that cannot work is a lie.
            disabled={status === 'offline'}
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
